import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Scale, Hammer, PackageCheck, Truck, Calculator, FileText, FileMinus, PackageOpen,
} from 'lucide-react';
import '../nativeitems/NativeItems.css';

// Shared list page for ALL the native documents (adjustments, builds,
// fulfillments, transfers, payments, invoices, credit memos, estimates,
// RMAs). Each config maps an API list endpoint to columns; the "+ New"
// button completes the NetSuite-style List | New pair in the Records nav.

const money = (n) => (n == null ? '—' : `${Number(n) < 0 ? '-' : ''}$${Math.abs(Number(n)).toFixed(2)}`);
const qty = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const stamp = (d) => (d ? new Date(d).toLocaleString() : '—');
const nsCell = (r) => r.ns_status + (r.ns_transaction_id ? ` (${r.ns_transaction_id})` : '');

const NON_POSTING_VOID = 'This document is non-posting: it is marked voided here and the NetSuite copy is deleted.';

// An amended document keeps its number, so the number alone no longer tells you
// which revision you are reading — the version rides next to it. Version 1 (the
// overwhelming majority of rows) renders nothing rather than a "v1" badge on
// every line. The amend reason is the tooltip, which is where it belongs: it is
// a sentence, not a column.
const numberCell = (key) => (r) => (
  <>
    <code>{r[key]}</code>
    {Number(r.amendment_version || 1) > 1 && (
      <span className="nit-tag" style={{ marginLeft: 6 }} title={r.amend_reason || 'amended'}>
        v{r.amendment_version}
      </span>
    )}
  </>
);

const CONFIGS = {
  adjustments: {
    title: 'Inventory Adjustments (Native)',
    icon: Scale,
    api: '/api/native-inventory-adjustments',
    rowsKey: 'adjustments',
    numberKey: 'adjustment_number',
    newPath: '/native-inventory-adjustments/new',
    newLabel: 'New Adjustment',
    amendPath: (r) => `/native-inventory-adjustments/new?amendOf=${r.id}`,
    columns: [
      { label: '#', get: numberCell('adjustment_number') },
      { label: 'Date', get: (r) => r.adjustment_date },
      { label: 'Reason', get: (r) => (r.reason || '').replace(/_/g, ' ') },
      { label: 'Lines', get: (r) => r.line_count, right: true },
      { label: 'Qty Δ', get: (r) => qty(r.total_qty_delta), right: true },
      { label: 'Value Δ', get: (r) => money(r.total_value_delta), right: true },
      { label: 'GL', get: (r) => r.gl_status || '—' },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
  },
  builds: {
    title: 'Assembly Builds (Native)',
    icon: Hammer,
    api: '/api/native-assembly-builds',
    rowsKey: 'builds',
    numberKey: 'build_number',
    newPath: '/native-assembly-builds/new',
    newLabel: 'New Build',
    amendPath: (r) => `/native-assembly-builds/new?amendOf=${r.id}`,
    columns: [
      { label: '#', get: numberCell('build_number') },
      { label: 'Date', get: (r) => r.build_date },
      { label: 'Assembly', get: (r) => <Link to={`/native-items/${encodeURIComponent(r.sku || r.item_id)}`}><code>{r.sku}</code></Link> },
      { label: 'Qty', get: (r) => qty(r.quantity), right: true },
      { label: 'Component cost', get: (r) => money(r.total_component_cost), right: true },
      { label: 'FG unit cost', get: (r) => money(r.fg_unit_cost), right: true },
      { label: 'GL', get: (r) => r.gl_status || '—' },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
    copyPath: (r) => `/native-assembly-builds/new?copyFrom=${r.id}`,
  },
  fulfillments: {
    title: 'Item Fulfillments (Native)',
    icon: PackageCheck,
    api: '/api/native-fulfillments',
    rowsKey: 'fulfillments',
    numberKey: 'fulfillment_number',
    newPath: '/native-fulfillments/new',
    newLabel: 'New Fulfillment',
    amendPath: (r) => `/native-fulfillments/new?amendOf=${r.id}`,
    columns: [
      { label: '#', get: numberCell('fulfillment_number') },
      { label: 'Date', get: (r) => r.ship_date },
      { label: 'NS SO', get: (r) => (r.ns_so_id ? <code>{r.ns_so_id}</code> : '—') },
      { label: 'Ship via', get: (r) => r.ship_method || '—' },
      { label: 'Lines', get: (r) => r.line_count, right: true },
      { label: 'COGS', get: (r) => money(r.total_cogs), right: true },
      { label: 'GL', get: (r) => r.gl_status || '—' },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
  },
  receipts: {
    title: 'Goods Receipts (Native)',
    icon: PackageOpen,
    api: '/api/native-receipts',
    rowsKey: 'receipts',
    numberKey: 'receipt_number',
    newPath: '/native-receipts/new',
    newLabel: 'Receive PO',
    amendPath: (r) => `/native-receipts/new?amendOf=${r.id}`,
    // Voiding a receipt walks the PO's received quantities back (the purchasing
    // package grew reverseReceiptLinkage for it), clears the GRNI accrual, takes
    // the quantity back out and deletes the cost layers. It refuses once a
    // vendor bill exists against the PO, or once the layers have been drawn on.
    voidMessage: 'This reverses all four legs: the PO\'s received quantities are walked back, the '
      + 'received-not-billed accrual is cleared, the quantity leaves stock and the cost layers are deleted.',
    columns: [
      { label: '#', get: numberCell('receipt_number') },
      { label: 'Date', get: (r) => (r.receipt_date || '').slice(0, 10) },
      { label: 'PO', get: (r) => (r.po_number ? <code>{r.po_number}</code> : '—') },
      { label: 'Vendor', get: (r) => r.vendor_name || '—' },
      { label: 'Value', get: (r) => money(r.total_value), right: true },
      // A receipt whose legs did not all post is 'needs_attention' — surfaced
      // here because the leg columns alone read as noise on a busy list.
      {
        label: 'Status',
        get: (r) => (
          (r.failed_legs || []).length > 0 || r.status === 'needs_attention'
            ? <span className="nit-tag nit-tag-warn">needs attention{(r.failed_legs || []).length > 0 ? `: ${r.failed_legs.join(', ')}` : ''}</span>
            : (r.status || '—')
        ),
      },
      { label: 'Quantity', get: (r) => r.movement_status || '—' },
      { label: 'GL', get: (r) => r.gl_status || '—' },
      { label: 'PO status', get: (r) => r.po_lifecycle || r.po_linkage_status || '—' },
      { label: 'NetSuite', get: (r) => (r.netsuite_item_receipt_id ? <code>{r.netsuite_item_receipt_id}</code> : 'none — native only') },
      { label: 'By', get: (r) => r.created_by || r.received_by || '—' },
    ],
  },
  transfers: {
    title: 'Inventory Transfers (Native)',
    icon: Truck,
    api: '/api/native-transfers',
    rowsKey: 'transfers',
    numberKey: 'transfer_number',
    newPath: '/native-transfers/new',
    newLabel: 'New Transfer',
    amendPath: (r) => `/native-transfers/new?amendOf=${r.id}`,
    columns: [
      { label: '#', get: numberCell('transfer_number') },
      { label: 'Date', get: (r) => r.transfer_date },
      { label: 'From → To (NS loc)', get: (r) => `${r.ns_from_location_id ?? '?'} → ${r.ns_to_location_id ?? '?'}` },
      { label: 'Lines', get: (r) => r.line_count, right: true },
      { label: 'Qty', get: (r) => qty(r.total_qty), right: true },
      { label: 'GL', get: (r) => r.gl_status || '—' },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
  },
  payments: {
    title: 'Customer Payments (Native)',
    icon: Calculator,
    api: '/api/native-payments',
    rowsKey: 'payments',
    numberKey: 'payment_number',
    newPath: '/native-payments/new',
    newLabel: 'New Payment',
    columns: [
      { label: '#', get: (r) => <code>{r.payment_number}</code> },
      { label: 'Date', get: (r) => r.payment_date },
      { label: 'Customer', get: (r) => r.customer_name || (r.ns_customer_id ? `NS ${r.ns_customer_id}` : '—') },
      { label: 'Method', get: (r) => r.payment_method || '—' },
      { label: 'Amount', get: (r) => money(r.amount), right: true },
      { label: 'Applied', get: (r) => money(r.applied_total), right: true },
      { label: 'GL', get: (r) => r.gl_status || '—' },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
  },
  invoices: {
    title: 'Invoices (Native)',
    icon: FileText,
    api: '/api/native-invoices',
    rowsKey: 'invoices',
    numberKey: 'invoice_number',
    newPath: '/native-invoices/new',
    newLabel: 'New Invoice',
    columns: [
      { label: '#', get: (r) => <code>{r.invoice_number}</code> },
      { label: 'Date', get: (r) => r.invoice_date },
      { label: 'Customer', get: (r) => r.customer_name || (r.ns_customer_id ? `NS ${r.ns_customer_id}` : '—') },
      { label: 'Lines', get: (r) => r.line_count, right: true },
      { label: 'Total', get: (r) => money(r.total), right: true },
      { label: 'GL', get: (r) => r.gl_status || '—' },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
  },
  creditMemos: {
    title: 'Credit Memos (Native)',
    icon: FileMinus,
    api: '/api/native-invoices/credit-memos',
    rowsKey: 'creditMemos',
    numberKey: 'credit_memo_number',
    newPath: '/native-invoices/new',
    newLabel: 'New Credit Memo',
    columns: [
      { label: '#', get: (r) => <code>{r.credit_memo_number}</code> },
      { label: 'Date', get: (r) => r.invoice_date },
      { label: 'Customer', get: (r) => r.customer_name || (r.ns_customer_id ? `NS ${r.ns_customer_id}` : '—') },
      { label: 'Lines', get: (r) => r.line_count, right: true },
      { label: 'Total', get: (r) => money(r.total), right: true },
      { label: 'GL', get: (r) => r.gl_status || '—' },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
  },
  estimates: {
    title: 'Estimates (Native)',
    icon: FileText,
    api: '/api/native-estimates/estimates',
    rowsKey: 'documents',
    numberKey: 'estimate_number',
    newPath: '/records/estimates/new',
    newLabel: 'New Estimate',
    // Non-posting and nothing draws against a quote, so an open estimate is
    // edited in place. Once the NetSuite copy exists the server refuses (the
    // NS leg is fire-once) — the link hides itself for those rows.
    editPath: (r) => '/records/estimates/' + r.id + '/edit',
    voidMessage: NON_POSTING_VOID,
    columns: [
      { label: '#', get: (r) => <code>{r.estimate_number}</code> },
      { label: 'Date', get: (r) => r.estimate_date },
      { label: 'Customer', get: (r) => r.customer_name || (r.ns_customer_id ? `NS ${r.ns_customer_id}` : '—') },
      { label: 'Expires', get: (r) => r.expires_date || '—' },
      { label: 'Lines', get: (r) => r.line_count, right: true },
      { label: 'Total', get: (r) => money(r.total), right: true },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
  },
  rmas: {
    title: 'Return Authorizations (Native)',
    icon: PackageOpen,
    api: '/api/native-estimates/return-authorizations',
    rowsKey: 'documents',
    numberKey: 'rma_number',
    newPath: '/records/return-authorizations/new',
    newLabel: 'New RMA',
    editPath: (r) => '/records/return-authorizations/' + r.id + '/edit',
    voidMessage: NON_POSTING_VOID,
    columns: [
      { label: '#', get: (r) => <code>{r.rma_number}</code> },
      { label: 'Date', get: (r) => r.rma_date },
      { label: 'Customer', get: (r) => r.customer_name || (r.ns_customer_id ? `NS ${r.ns_customer_id}` : '—') },
      { label: 'From NS SO', get: (r) => (r.created_from_ns_so_id ? <code>{r.created_from_ns_so_id}</code> : '—') },
      { label: 'Lines', get: (r) => r.line_count, right: true },
      { label: 'Total', get: (r) => money(r.total), right: true },
      { label: 'NetSuite', get: nsCell },
      { label: 'By', get: (r) => r.created_by || '—' },
      { label: 'Created', get: (r) => stamp(r.created_at) },
    ],
  },
};

export default function NativeDocsList({ kind }) {
  const cfg = CONFIGS[kind];
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [voiding, setVoiding] = useState({});
  // Amend is gated server-side on NATIVE_AMEND_ENABLED; the list endpoint says
  // so, and the button stays hidden while it is off rather than offering an
  // action that will 403.
  const [amendEnabled, setAmendEnabled] = useState(false);
  const Icon = cfg.icon;

  const load = () => {
    axios.get(cfg.api)
      .then(({ data }) => {
        setRows(data[cfg.rowsKey] || []);
        setAmendEnabled(data.amendEnabled === true);
      })
      .catch((err) => setError(err.response?.data?.error || err.message));
  };
  useEffect(() => {
    setRows(null);
    setError(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const voidDoc = async (r, label) => {
    const consequence = cfg.voidMessage || 'This posts a full reversal (inventory, costing, GL, NetSuite).';
    if (!window.confirm(`Void ${label}? ${consequence}`)) return;
    setVoiding((v) => ({ ...v, [r.id]: true }));
    setError(null);
    try {
      await axios.post(`${cfg.api}/${r.id}/void`);
      load();
    } catch (err) {
      setError(`${label}: ${err.response?.data?.error || err.message}`);
    } finally {
      setVoiding((v) => ({ ...v, [r.id]: false }));
    }
  };
  const docLabel = (r) => (cfg.numberKey && r[cfg.numberKey]) || r.id;
  const isVoided = (r) => r.status === 'voided' || r.reason === 'void';

  // Amend opens the document's own New screen prefilled (?amendOf=<id>), which
  // is where the edit is made and where the reason is collected — this button
  // only states the consequence and hands over. Confirming here rather than
  // only at submit means nobody lands on a prefilled form without knowing that
  // saving it reverses the original.
  const startAmend = (r, label) => {
    const ok = window.confirm(
      `Amend ${label}?\n\n`
      + 'This does NOT edit the document. It reverses and reposts: the original is voided with a full '
      + 'reversal, and your corrected version is posted under the same number. Both stay in the GL.\n\n'
      + 'The next screen opens prefilled with this document; you make the changes and give a reason there.'
    );
    if (!ok) return;
    window.location.assign(cfg.amendPath(r));
  };

  return (
    <div className="nit-page">
      <div className="nit-item-head" style={{ marginTop: 8 }}>
        <h1><Icon size={20} style={{ verticalAlign: '-3px', marginRight: 6 }} />{cfg.title}</h1>
        <Link to={cfg.newPath} className="nit-btn nit-btn-primary" style={{ marginLeft: 'auto', textDecoration: 'none' }}>
          <Plus size={14} /> {cfg.newLabel}
        </Link>
      </div>

      {error && <div className="nit-error">{error}</div>}
      {!rows && !error && <div className="nit-loading">Loading…</div>}
      {rows && rows.length === 0 && (
        <div className="nit-empty">None posted yet — the first one is one click away.</div>
      )}
      {rows && rows.length > 0 && (
        <div className="nit-card">
          <div className="nit-table-wrap">
            <table className="nit-table">
              <thead>
                <tr>
                  {cfg.columns.map((c) => <th key={c.label} className={c.right ? 'r' : ''}>{c.label}</th>)}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={isVoided(r) ? 'nit-inactive-row' : ''}>
                    {cfg.columns.map((c) => <td key={c.label} className={c.right ? 'r' : ''}>{c.get(r)}</td>)}
                    <td className="r" style={{ whiteSpace: 'nowrap' }}>
                      {cfg.copyPath && (
                        <Link to={cfg.copyPath(r)} className="nit-btn" style={{ textDecoration: 'none', padding: '3px 8px', fontSize: 12, marginRight: 4 }}>Copy</Link>
                      )}
                      {cfg.editPath && !isVoided(r) && r.ns_transaction_id == null && (
                        <Link to={cfg.editPath(r)} className="nit-btn" style={{ textDecoration: 'none', padding: '3px 8px', fontSize: 12, marginRight: 4 }}>Edit</Link>
                      )}
                      {cfg.amendPath && amendEnabled && !isVoided(r) && (
                        <button
                          className="nit-btn"
                          style={{ padding: '3px 8px', fontSize: 12, marginRight: 4 }}
                          onClick={() => startAmend(r, docLabel(r))}
                        >
                          Amend
                        </button>
                      )}
                      {cfg.noVoid ? null : isVoided(r) ? (
                        <span className="nit-muted" style={{ fontSize: 12 }}>{r.status === 'voided' ? 'voided' : 'void doc'}</span>
                      ) : (
                        <button
                          className="nit-btn"
                          style={{ padding: '3px 8px', fontSize: 12 }}
                          disabled={!!voiding[r.id]}
                          onClick={() => voidDoc(r, docLabel(r))}
                        >
                          {voiding[r.id] ? '…' : 'Void'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="nit-caption">{rows.length} most recent shown.</p>
        </div>
      )}
    </div>
  );
}
